import { hexToRGB } from "@shared/utils/misc";
import React, { ReactNode, useContext } from "react";
import NewLookPlanFeatures from "features/settings/Subscription/NewLook/NewLookPlanFeatures";
import styled, { css } from "styled-components";
import { useHistory } from "react-router-dom";
import { Check } from "lucide-react";
import {
  FormattedPlanDetails,
  LanguageNameSpaces,
  SubscriptionDuration,
  UsageType,
} from "@shared/types";
import { useUserUsageTypes } from "@shared/utils/hooks/misc";
import { SUBSCRIPTION_STEPS } from "@shared/features/settings/subscription/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import Button from "common/Buttons/Button";
import { Pill } from "common/NewLook";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens.ts";
import { formatMoney } from "features/settings/Subscription/NewLook/formatMoney.ts";
import { getPurchaseSubscriptionPath } from "features/settings/common/utils";
import { BuySubscriptionContext } from "features/settings/Subscription/BuySubscription.context";
import usePlanCard from "features/settings/Subscription/usePlanCard.ts";
import { getNewLookQuotaPlanFeature } from "features/settings/Subscription/utils";
import DowngradeQuotaModal from "features/settings/Subscription/DowngradeQuotaModal";
import ConfirmDowngradeModal from "features/settings/Subscription/ConfirmDowngradeModal";
import AutoRenewalModal from "features/settings/Subscription/AutoRenewalModal";
import CancelSubscriptionModal from "features/settings/Billing/CancelSubscriptionModal";

interface NewLookPlanCardProps {
  plan: FormattedPlanDetails;
  isUpgrade?: boolean;
  promoCodeDiscount?: Record<SubscriptionDuration, number>;
  description?: string;
  isEnterprise?: boolean;
  /**
   * Business plans in a narrow column (next to a tier card, or on phones):
   * stacked like the tier cards instead of the wide price/action row.
   */
  isCompact?: boolean;
}

/**
 * A plan in the new-look plan picker: name and state, the price per month
 * with its billing note, one action, then what the plan includes.
 */
const NewLookPlanCard = ({
  plan,
  isUpgrade,
  promoCodeDiscount,
  description,
  isEnterprise,
  isCompact,
}: NewLookPlanCardProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BILLING);
  const { subscriptionParams, promoCode } = useContext(BuySubscriptionContext)!;
  const queryDuration = subscriptionParams.duration;
  const history = useHistory();
  const usageTypes = useUserUsageTypes();

  const onUpgradeClick = () => {
    const isNextPathUpgrade = !user?.isPurchaseSubscription();
    history.push(
      getPurchaseSubscriptionPath({
        isUpgrade: isNextPathUpgrade,
        duration: isNextPathUpgrade ? undefined : queryDuration,
        isAllPlans: true,
        step: SUBSCRIPTION_STEPS.DURATION,
        plan: plan.subscription_plan_type,
        promoCode,
      })
    );
  };

  const {
    user,
    isLoading,
    isCurrentPlan,
    isDowngradePlan,
    upgradePrice,
    isPremium,
    yearlyAmountDiscount,
    getMonthlyPrice,
    getPurchaseButtonText,
    getPlanFeatures,
    onBuyClick,
    confirmDowngradeModalProps,
    autoRenewalModalProps,
    downgradeQuotaModalProps,
    cancelSubscriptionModalProps,
    showFreeTag,
    isErrorButton,
  } = usePlanCard({
    plan,
    isUpgrade,
    promoCodeDiscount,
    getQuotaPlanFeature: getNewLookQuotaPlanFeature,
    onUpgradeClick,
  });

  // The recommended plan carries the page's one primary action — unless
  // taking it would be a downgrade.
  // Premium gets a light emphasis (a soft accent ring and a tag) whatever
  // the user's current plan; it is never louder than the current plan.
  const isFeatured = isPremium && !isEnterprise && !isCurrentPlan;
  const isHighlighted =
    isPremium && !isEnterprise && !isDowngradePlan && !isCurrentPlan;
  const onlyShowTotal = isUpgrade && (plan.upgradePlan?.days_left || 0) <= 30;

  const statusPill = isCurrentPlan && (
    <Pill tone="blue" icon={Check}>
      {t("plan_card.current_plan")}
      {user?.is_trial ? ` ${t("plan_card.trial")}` : ""}
    </Pill>
  );

  const priceNote: ReactNode[] = [];
  if (isUpgrade && !onlyShowTotal) {
    priceNote.push(
      <Muted key="total">
        ${formatMoney(upgradePrice)} {t("plan_card.total")}
      </Muted>
    );
  }
  if (yearlyAmountDiscount) {
    priceNote.push(
      <Saving key="saving">
        {t("plan_card.you_save_yearly", {
          amount: formatMoney(yearlyAmountDiscount),
        })}
      </Saving>
    );
  }
  if (showFreeTag) {
    priceNote.push(
      <Pill key="free" tone="green">
        {t("plan_card.no_cost")}
      </Pill>
    );
  }

  const price = (
    <PriceBlock>
      {isEnterprise && (
        <StartingFrom>{t("plan_card.starting_from")}</StartingFrom>
      )}
      <PriceRow>
        <Price>
          ${formatMoney(onlyShowTotal ? upgradePrice : getMonthlyPrice())}
        </Price>
        {!onlyShowTotal && <Period>{t("duration_radio.per_month")}</Period>}
      </PriceRow>
      {priceNote.length > 0 && <PriceNote>{priceNote}</PriceNote>}
    </PriceBlock>
  );

  const action = (
    <ActionButton
      type={isHighlighted ? "primary" : "default"}
      onClick={onBuyClick}
      loading={isLoading}
      $error={isErrorButton}
      $isWide={isEnterprise && !isCompact}
    >
      {getPurchaseButtonText()}
    </ActionButton>
  );

  const modals = (
    <>
      <DowngradeQuotaModal {...downgradeQuotaModalProps} />
      <ConfirmDowngradeModal {...confirmDowngradeModalProps} />
      <AutoRenewalModal {...autoRenewalModalProps} />
      <CancelSubscriptionModal {...cancelSubscriptionModalProps} />
    </>
  );

  const features = getPlanFeatures() ?? [];
  const featureList = (list: ReactNode[], columns: 1 | 2) =>
    list.length > 0 && (
      <Features $columns={columns}>
        {list.map((feature, index) => (
          <Feature key={index}>
            <FeatureCheck>
              <Check size={14} strokeWidth={2} />
            </FeatureCheck>
            {feature}
          </Feature>
        ))}
      </Features>
    );

  if (isEnterprise) {
    // What sets a business plan apart from Premium: its API slots, plus the
    // client invite, invoicing and whitelabel extras listed after the usage
    // types.
    const keyFeatures = [
      features[usageTypes.indexOf(UsageType.CLIENT)],
      ...features.slice(usageTypes.length),
    ].filter(Boolean);
    return (
      <Card $isCurrent={isCurrentPlan} $isEnterprise={true}>
        <Header>
          <Name>{plan.subscription_type}</Name>
          {statusPill}
        </Header>
        {description && <Description>{description}</Description>}
        <EnterpriseOffer $isCompact={isCompact}>
          {price}
          {action}
        </EnterpriseOffer>
        {featureList(keyFeatures, isCompact ? 1 : 2)}
        {modals}
      </Card>
    );
  }

  return (
    <Card $isCurrent={isCurrentPlan} $isFeatured={isFeatured}>
      <Header>
        <Name>{plan.subscription_type}</Name>
        {statusPill ||
          (isFeatured && (
            <Pill tone="blue">{t("plan_features.most_popular")}</Pill>
          ))}
      </Header>
      {price}
      {action}
      {/* Phone: the tier cards sit in a swipe carousel, so each card
          lists everything it includes (no show/hide toggle). */}
      <CardFeatures>
        <NewLookPlanFeatures plan={plan} dense={true} />
      </CardFeatures>
      {modals}
    </Card>
  );
};

const CardFeatures = styled.div`
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
`;

export default NewLookPlanCard;

/**
 * A plan on a panel (hairline, no shadow). The current plan is the selected
 * one: a 2px accent ring and its pill.
 */
const Card = styled.div<{
  $isCurrent?: boolean;
  $isEnterprise?: boolean;
  $isFeatured?: boolean;
}>`
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 0;
  height: 100%;
  padding: 20px;
  @media (max-width: 600px) {
    padding: 16px;
  }
  border-radius: ${(p) => p.theme.radii.card}px;
  border: ${(p) => p.theme.borders.panel};
  background: ${(p) => getNewLookTokens(p.theme).panel};
  box-shadow: ${(p) => p.theme.shadows.panel};
  transition: border-color 120ms, box-shadow 120ms;
  &:hover {
    ${(p) =>
      p.theme.isDarkMode
        ? `border-color: ${getNewLookTokens(p.theme).hairlineStrong};`
        : `box-shadow: ${p.theme.shadows.hoverCard};`}
  }
  ${(p) =>
    p.$isFeatured &&
    css`
      /* A soft filled surface (no ring), so it never reads like the
         current-plan outline. */
      background: ${p.theme.isDarkMode
        ? hexToRGB(p.theme.colors.primaryBlue, 0.1)
        : "#F5F8FF"};
      .ant-btn-default {
        background: ${p.theme.colors.cardBgColor};
      }
    `}
  ${(p) =>
    p.$isCurrent &&
    css`
      &,
      &:hover {
        border-color: ${getNewLookTokens(p.theme).accent};
        box-shadow: 0 0 0 1px ${getNewLookTokens(p.theme).accent},
          ${p.theme.shadows.dashboardCard};
      }
    `}
`;
const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 22px;
  flex-wrap: wrap;
`;
const Name = styled.h3`
  margin: 0;
  flex: 1 1 auto;
  min-width: 0;
  ${newLookType.panelTitle};
  color: ${(p) => getNewLookTokens(p.theme).ink};
`;
const Description = styled.p`
  margin: 8px 0 0;
  ${newLookType.body};
  color: ${(p) => getNewLookTokens(p.theme).secondary};
`;
const PriceBlock = styled.div`
  margin-top: 12px;
  @media (max-width: 600px) {
    margin-top: 6px;
  }
`;
const StartingFrom = styled.div`
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
const PriceRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 6px;
`;
const Price = styled.span`
  ${newLookType.display};
  color: ${(p) => getNewLookTokens(p.theme).ink};
  @media (max-width: 600px) {
    font-size: 30px;
    line-height: 36px;
  }
`;
const Period = styled.span`
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
const PriceNote = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 12px;
  min-height: 20px;
  margin-top: 4px;
  ${newLookType.meta};
`;
const Muted = styled.span`
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
const Saving = styled.span`
  color: ${(p) => getNewLookTokens(p.theme).up};
  font-weight: 500;
`;
const ActionButton = styled(Button)<{ $isWide?: boolean }>`
  &&& {
    width: 100%;
    height: 40px;
    margin-top: 20px;
  }
  @media (max-width: 600px) {
    &&& {
      margin-top: 14px;
    }
  }
  ${(p) =>
    p.$isWide &&
    css`
      &&& {
        width: auto;
        min-width: 180px;
        margin-top: 0;
      }
      @media (max-width: 500px) {
        &&& {
          width: 100%;
          margin-top: 20px;
        }
      }
    `}
`;
const Features = styled.ul<{ $columns: 1 | 2 }>`
  display: grid;
  grid-template-columns: repeat(${(p) => p.$columns}, minmax(0, 1fr));
  gap: 12px 32px;
  margin: 20px 0 0;
  padding: 20px 0 0;
  list-style: none;
  border-top: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  @media (max-width: 500px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;
// The quota reads first: the number in ink, in its own aligned column, then
// the label in a softer grey, and any extra quota as a meta line below.
const Feature = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  min-width: 0;
  ${newLookType.body};
  color: ${(p) => getNewLookTokens(p.theme).secondary};
  > div {
    min-width: 0;
    flex: 1;
    gap: 0 5px !important;
    line-height: 20px;
  }
  .plan-quota-number {
    min-width: 22px;
    font-weight: 500 !important;
    font-variant-numeric: tabular-nums;
    color: ${(p) => getNewLookTokens(p.theme).ink};
  }
  .plan-extra-quota {
    flex-basis: 100%;
    padding-left: 27px;
    ${newLookType.meta};
    color: ${(p) => getNewLookTokens(p.theme).muted};
  }
`;
/** A plain check in the label grey (no tinted circle). */
const FeatureCheck = styled.span`
  flex: none;
  display: inline-flex;
  margin-top: 3px;
  color: ${(p) => getNewLookTokens(p.theme).accent};
`;
/**
 * Business plans: the price and the action share one row under the pitch
 * (compact cards stack them like a tier card). Pushed to the bottom so the
 * offers of side-by-side business cards line up.
 */
const EnterpriseOffer = styled.div<{ $isCompact?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px 24px;
  margin-top: auto;
  padding-top: 20px;
  ${PriceBlock} {
    margin-top: 0;
  }
  @media (max-width: 500px) {
    gap: 0;
  }
  ${(p) =>
    p.$isCompact &&
    css`
      display: block;
    `}
`;
